import React, { useState, useEffect } from 'react';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { event } from '@uhg-abyss/web/tools/event';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { FormFields } from './FormFields';
import {
  resetLeadGenerationApi,
  useLeadGenerationApi,
} from '@src/hooks/useLeadGenerationApi';
import { resetCalendarAPI } from '@src/hooks/useCalendarApi';
import { useHolidayApi } from '@src/hooks/useHolidayApi';
import { ErrorComponent } from '@src/common/Error';

const Wrapper = styled('div', {});
const ModalDescription = styled('p', {
  fontSize: '15px',
});

export const SubscribeToAPIModal = ({ showModal, modalOnClose }) => {
  const [, resetLeadGeneration] = resetLeadGenerationApi();
  const [leadGenerationApi, postLeadGenerationApi] = useLeadGenerationApi();
  const [, resetCalendarAPIResponse] = resetCalendarAPI();
  const [modalSize, setModalSize] = useState('lg');
  const [modalTitle, setModalTitle] = useState(
    'Request a meeting with an API consultant'
  );
  const [displaySuccess, setDisplaySuccess] = useState(false);
  const [displayError, setDisplayError] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState('');

  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });
  const dsUserInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const form = useForm();
  const isFQHC = form.getValues('isFQHC');
  const [holidayDateApi, setHolidayDate] = useHolidayApi();
  const jobName = 'request a meeting with an api consultant';
  const modalEventName = 'meeting request with an api consultant modal';

  const consultantData = useSelector((state) => {
    return state.GET_CALANDAR_TIMES.data;
  });

  const getConsultantName = (consultantId) => {
    if (consultantData && Object.keys(consultantData).length > 0) {
      const { consultants } = consultantData;
      const consultant = consultants.find((el) => {
        return el.consultantId === consultantId;
      });

      return `${consultant.firstName} ${consultant.lastNameInitials}`;
    }

    return consultantId;
  };

  useEffect(() => {
    form.setValue('email', userInfo.email);
  }, [userInfo]);

  useEffect(() => {
    if (showModal) {
      resetLeadGeneration();
      const { data = {}, error } = holidayDateApi || {};
      if (!Object.keys(data)?.length || error) {
        setHolidayDate({ userInfo });
      }
      event('MODAL_VIEW', {
        event,
        modalName: modalEventName,
      });
    }
  }, [showModal]);

  const closeModalClicked = () => {
    modalOnClose(false);
    setDisplayError(false);
    resetLeadGeneration();
    resetCalendarAPIResponse();
    form.reset();
    form.setValue('email', userInfo.email);
    setDisplaySuccess(false);
    setModalSize('lg');
    setModalTitle('Request a meeting with an API consultant');

    event('MODAL_CLOSE', {
      event,
      modalName: modalEventName,
    });
    if (!leadGenerationApi.data) {
      event('JOB_CANCEL', {
        event,
        jobName,
      });
    }
  };

  const orgTypeMapping = {
    P: 'Care Provider',
    B: 'Revenue Cycle Management',
    V: 'Clearinghouse',
  };
  const errorMessage = 'Missing or invalid field(s)';
  const errorType = 'form';
  const onError = (errors) => {
    const errorFields = Object.keys(errors).join();
    event('FORM_ERROR', {
      event,
      errorMessage,
      errorFields,
      errorType,
    });
  };

  const onSubmit = (data) => {
    let requestObj = {
      orgName: data.orgName || dsUserInfo.orgName,
      orgType: data.orgType || orgTypeMapping[dsUserInfo?.orgType],
      orgWebsite: data.orgWebsite || '',
      orgEmail: data.email,
      phoneNo: data.phone,
      apis: data.selectedAPIs,
      preferredDate: data.preferredMeetingDate,
      preferredTime: data.preferredMeetingTime,
      preferredConsultantId: data.consultant,
    };
    if (dsUserInfo?.status === 'ACTIVE') {
      requestObj = {
        ...requestObj,
        orgId: dsUserInfo?.orgId,
        clientId: dsUserInfo?.clientId,
        additionalComments: data.businessValue,
        fqhc: isFQHC,
      };
    } else {
      requestObj = {
        ...requestObj,
        emrVendor: data.emrVendor || '',
        rcmSupportedServices: data.rcmSupportedServices || [],
        isItResourceAvailable: data.isItResourceAvailable,
        itResourceType: data.itResourceType || '',
        itCompanyName: data.itCompanyName || '',
        timeline: data.timeline,
        tinCount: data.tinCount,
        primaryTaxId: data.primaryTaxId,
        automations: data.automations || '',
        intake: true,
      };
    }

    const requestedAPI = requestObj.apis.join();
    const organizationName = requestObj.orgName;
    const organizationType = requestObj.orgType;

    const preferredMeetingDateTime = `${requestObj.preferredDate} ${requestObj.preferredTime} EST`;

    const supportedRcmServices =
      requestObj.rcmSupportedServices?.join(', ') || '';
    event('JOB_REQUEST_API_SUBSCRIPTION_SUBMIT_INTAKE', {
      event,
      jobName,
      organizationName,
      fullName: `${userInfo.firstName} ${
        userInfo.lastName || userInfo.family_name
      }`,
      organizationType,
      supportedRcmServices,
      emrSystem: requestObj.emrVendor || '',
      organizationWebsite: requestObj.orgWebsite,
      emailAddress: requestObj.orgEmail,
      phoneNumber: requestObj.phoneNo || '',
      requestedAPI,
      hasITResources: requestObj.isItResourceAvailable || '',
      itResourceType: requestObj.itResourceType || '',
      outsourcedCompanyName: requestObj.itCompanyName || '',
      apiWorkflowAutomationTimeline: requestObj.timeline || '',
      numberOfTaxIds: requestObj.tinCount || '',
      providerTaxId: requestObj.primaryTaxId || '',
      useOfAutomatedTransactions: requestObj.automations || '',
      preferredMeetingDateTime,
      preferredConsultant:
        getConsultantName(requestObj.preferredConsultantId) || '',
      isDesignatedGroup: requestObj.fqhc || '',
      businessValue: requestObj.additionalComments || '',
    });

    postLeadGenerationApi({
      variables: { requestObj },
      userInfo,
    });
  };

  useEffect(() => {
    if (
      !leadGenerationApi.loading &&
      leadGenerationApi.data?.status &&
      leadGenerationApi.data?.status === 200 &&
      showModal
    ) {
      event('JOB_COMPLETE', {
        event,
        jobName,
      });
      setModalSize('md');
      setDisplaySuccess(true);
      setModalTitle('Thank you for your submission');
    }
    if (leadGenerationApi?.error) {
      setDisplayError(true);
      const leadGenerationErrorMessage = leadGenerationApi?.data?.message || '';
      if (leadGenerationErrorMessage) {
        setServerErrorMessage(leadGenerationErrorMessage);
      } else {
        setServerErrorMessage(
          'Your request was unsuccessful, please try again.'
        );
      }
    }
  }, [leadGenerationApi]);

  const getModalDescription = () => {
    if (dsUserInfo?.status === 'ACTIVE') {
      return 'Please complete the form below to provide us with information about your organization and the APIs of interest. An API Consultant will meet with you on your selected date and time.';
    }
    return 'Thank you for your interest. To help us determine the best solution for your team, please complete the form below. An API Consultant will meet with you on your selected date and time.';
  };

  return (
    <Wrapper>
      <Modal
        size={modalSize}
        title={modalTitle}
        isOpen={showModal}
        onClose={closeModalClicked}
        closeOnClickOutside={false}
        css={{
          'abyss-modal-content-title': { 'font-size': '25px !important' },
        }}
      >
        {displaySuccess ? (
          <Modal.Section>
            <ModalDescription id="reminder">
              Please be sure to set your reminder, we look forward to meet with
              you on your selected Date and Time.
            </ModalDescription>
          </Modal.Section>
        ) : (
          <Modal.Section>
            <ModalDescription id="APISubscription">
              {getModalDescription()}
            </ModalDescription>
            <FormProvider state={form} onSubmit={onSubmit} onError={onError}>
              <FormFields />
              <Layout.Group alignLayout="right">
                <Button
                  variant="outline"
                  onClick={closeModalClicked}
                  style={{
                    marginTop: '15px',
                    marginRight: '15px',
                    fontSize: '15px',
                  }}
                  id="cancel"
                >
                  Cancel
                </Button>
                <Button
                  isLoading={leadGenerationApi.loading}
                  ariaLoadingLabel="Request a meeting with an API consultant"
                  type={leadGenerationApi.loading ? 'button' : 'submit'}
                  style={{ marginTop: '15px', fontSize: '15px' }}
                  id="requestNow"
                >
                  Request Now
                </Button>
              </Layout.Group>
              <Layout.Stack alignLayout="right">
                {displayError ? (
                  <ErrorComponent id="serverError">
                    {serverErrorMessage}
                  </ErrorComponent>
                ) : null}
              </Layout.Stack>
            </FormProvider>
          </Modal.Section>
        )}
      </Modal>
    </Wrapper>
  );
};

SubscribeToAPIModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  modalOnClose: PropTypes.func.isRequired,
};
